
import Vue from 'vue'
// Components
import { VDatePicker, VIcon, VMenu, VTextField } from 'vuetify/lib'
import JcdcDateRanges from '@/components/jcdc/JcdcDateRanges.vue'
import JcdcProgressLinear from '@/components/jcdc/JcdcProgressLinear.vue'
// Utilities
import { formatDateValue, formatDisplayDate } from '@/util/formatters'
// Types
import { VuetifyIcon } from 'vuetify/types/services/icons'
// Dependencies
import dayjs, { ManipulateType } from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'
dayjs.extend(isBetween)
import minMax from 'dayjs/plugin/minMax'
dayjs.extend(minMax)
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
dayjs.extend(isSameOrBefore)
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
dayjs.extend(isSameOrAfter)

export interface maxRange {
  value: number
  unit?: ManipulateType
}

export default Vue.extend({
  name: 'JcdcDatePicker',
  components: {
    VMenu,
    VTextField,
    VDatePicker,
    VIcon,
    JcdcDateRanges,
    JcdcProgressLinear,
  },
  props: {
    color: { type: String, default: 'primary' },
    attach: { type: Boolean, default: false },
    label: { type: String, required: true },
    required: { type: Boolean, default: false },
    range: { type: Boolean, default: false },
    minDate: { type: [String, Date, Number, Object], default: '' },
    maxDate: { type: [String, Date, Number, Object], default: '' },
    maxRange: { type: Object as () => maxRange, default: undefined },
    current: { type: Boolean, default: false },
    last: { type: Boolean, default: false },
    pyRange: { type: Number, default: 0 },
    rolling: { type: Boolean, default: false },
    hint: { type: String, default: '' },
    loading: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    readonly: { type: Boolean, default: false },
    defaultValue: { type: [String, Array], default: '' },
    dense: { type: Boolean, default: false },
    value: { type: Boolean, default: false },
    allowedDates: { type: Function, default: null },
  },
  data() {
    return {
      dateValue: '', // Single date picker value
      dateArray: [] as string[], // Date range picker value
      dateFormatted: '', // Text filed value
      menu: this.value, // Menu state
      rangeKey: 0, // Re-renders date-picker to display current dates when dates change
      persistentHint: false,
      invalidHint: '',
      calcMaxDate: this.maxDate,
      calcMinDate: this.minDate,
      inValid: false,
      colorValue: this.color,
    }
  },
  computed: {
    computedRules(): Array<(data: string) => void> {
      // Rules Array
      const ruleArray: Array<(data: string) => void> = []
      // Required
      if (this.required) {
        ruleArray.push(
          (v: string) => Boolean(v) || `${this.label} is required.`
        )
      }
      // Single Date Picker
      if (!this.range) {
        // Valid Date
        ruleArray.push(
          (v: string) =>
            (v ? dayjs(v, 'M/D/YYYY', true).isValid() : !v) ||
            `Please enter a valid date.`
        )
        // Allowed Dates
        if (this.allowedDates) {
          ruleArray.push(
            (v: string) =>
              (v ? this.validateAllowedDates(v) : !v) ||
              `Invalid. ${this.dateHint}`
          )
        }
      }
      // Range Date Picker
      if (this.range) {
        // Valid Date
        ruleArray.push((v: string) => {
          const pattern =
            /^$|(0[1-9]|1[012])[- /](0[1-9]|[12][0-9]|3[01])[- /](19|20)\d\d\s+[-]\s+(0[1-9]|1[012])[- /](0[1-9]|[12][0-9]|3[01])[- /](19|20)\d\d?$/
          return (
            (v ? pattern.test(v) : !v) ||
            `Please enter a valid date range: DD/MM/YYYY - DD/MM/YYYY`
          )
        })
        // Allowed Dates
        if (this.allowedDates) {
          ruleArray.push((v: string) => {
            const [date1, date2] = v.split('-')
            return (
              (v
                ? this.validateAllowedDates(date1) &&
                  this.validateAllowedDates(date2)
                : !v) || `Invalid. ${this.dateHint}`
            )
          })
        }
        // Max Range
        if (this.maxRange) {
          ruleArray.push(
            (v: string) =>
              (v ? this.validateMaxRange(v) : !v) || `Invalid. ${this.dateHint}`
          )
        }
      }
      return ruleArray
    },
    dateHint(): string {
      // Invalid
      if (this.invalidHint) return `${this.invalidHint}`
      // Hint for Min/Max dates
      const formattedMinDate = formatDisplayDate(this.minDate),
        formattedMaxDate = formatDisplayDate(this.maxDate)
      // MIN MAX DATE HINTS
      let minMaxDateHint = ''
      // Min/Max
      if (this.minDate && this.maxDate)
        minMaxDateHint = `${
          this.hint ? ', ' : `${this.range ? ' Dates are' : ' Date is'}`
        }
         limited to ${formattedMinDate} - ${formattedMaxDate}.`
      //  Min
      else if (this.minDate)
        minMaxDateHint = `${
          this.hint ? ', ' : `${this.range ? ' Dates' : ' Date'} must be`
        } on or after ${
          dayjs(this.minDate).isSame(new Date(), 'day')
            ? 'today'
            : formattedMinDate
        }.`
      // Max
      else if (this.maxDate)
        minMaxDateHint = `${
          this.hint ? ', ' : `${this.range ? ' Dates' : ' Date'} must be`
        } on or before ${
          dayjs(this.maxDate).isSame(new Date(), 'day')
            ? 'today'
            : formattedMaxDate
        }.`
      // MAX RANGE HINTS
      let maxRangeHint = ''
      if (this.maxRange)
        maxRangeHint = ` Date range is limited to ${this.numberToText(
          this.maxRange['value']
        )} ${this.unitToText(this.maxRange['unit'])}${
          this.maxRange['value'] > 1 ? 's' : ''
        }.`
      return `${this.hint}${minMaxDateHint}${maxRangeHint}`
    },
    calendarIcon(): VuetifyIcon {
      if (this.disabled || this.readonly || this.loading) return '$calendarLock'
      else if (this.menu) return '$calendarRemove'
      else if (this.range) return '$calendarRange'
      return '$calendar'
    },
    attachID() {
      return `_${Math.random().toString(36).substr(2, 9)}`
    },
  },
  watch: {
    defaultValue(val) {
      this.dateChange(val)
    },
  },
  mounted() {
    if (this.defaultValue) {
      this.dateChange(this.defaultValue)
    }
    this.menu = this.value
  },
  methods: {
    formatDateValue,
    formatDisplayDate,
    onInput(event) {
      // Triggered when form reset it clicked.
      if (!event) {
        const nullDate = this.range ? '-' : null
        this.dateChange(nullDate)
      }
    },
    onFocus() {
      this.readonlyHandler()
    },
    onBlur() {
      if (this.readonly) {
        this.colorValue = this.color
      }
    },
    readonlyHandler() {
      this.readonly
        ? (this.colorValue = 'error')
        : (this.colorValue = this.color)
    },
    dateChange(date) {
      // SINGLE
      if (!this.range) {
        this.resetHints()
        const checkedDate = this.checkMinMax(date)
        // Check if date has changed
        if (
          !dayjs(date).isSame(this.dateValue, 'day') ||
          !dayjs(checkedDate).isSame(this.dateValue, 'day')
        ) {
          this.dateValue = formatDateValue(checkedDate)
          this.dateFormatted = formatDisplayDate(checkedDate)
          this.$emit('change', this.dateValue)
        }
        this.menu = false
      }
      // RANGE
      else {
        this.resetHints()
        const tempDateArray: string[] = []
        let [date1, date2] = date.split('-')
        // Check date1 against min/max, etc
        date1 = this.checkMinMax(date1)
        tempDateArray.push(formatDateValue(date1))
        if (date1 == '') {
          this.dateArrayChange(tempDateArray)
        } else {
          // update min/max date from max range
          if (this.maxRange) this.maxRangeCalc(date1, this.maxRange)
          // Check date two
          date2 = this.checkMinMax(date2)
          tempDateArray.push(formatDateValue(date2))
          tempDateArray.sort()
          this.dateArrayChange(tempDateArray)
        }
      }
    },
    rangeClicked(date) {
      if (this.maxRange) this.maxRangeCalc(date[0], this.maxRange)
      const tempDateArray: string[] = date.map((x) => {
        x = this.checkMinMax(x)
        x = formatDateValue(x)
        return x
      })
      this.dateArray = tempDateArray
      this.dateArrayChange(this.dateArray)
    },
    dateArrayChange(date, eventType?) {
      // Clear custom errors for click events in the calendar
      if (eventType == 'clickEvent') this.resetHints()
      this.dateArray = date
      // Format date string
      if (date[0] === '' || date.length === 0) {
        this.dateFormatted = ''
        this.$emit('change', undefined)
        // range calc
        this.resetRangeCalc()
      } else if (date[1] === '' || date.length === 1) {
        this.dateFormatted = `${formatDisplayDate(date[0])} - `
        // range calc
        if (this.maxRange) this.maxRangeCalc(date[0], this.maxRange)
      } else {
        date.sort()
        this.dateFormatted = `${formatDisplayDate(
          date[0]
        )} - ${formatDisplayDate(date[1])}`
        this.menu = false
        this.$emit('change', date)
        // range calc
        this.resetRangeCalc()
      }
      ++this.rangeKey // Update calendar display
    },
    checkMinMax(date) {
      // Date exists
      if (!date) return ''
      // Change dates to the Min Date
      else if (!this.validateMin(date) && !this.allowedDates) {
        this.invalidHint = this.dateHint
        this.persistentHint = true
        return this.calcMinDate
      }
      // Change dates to the Max Date
      else if (!this.validateMax(date) && !this.allowedDates) {
        this.invalidHint = this.dateHint
        this.persistentHint = true
        return this.calcMaxDate
      }
      return date
    },
    resetHints() {
      this.invalidHint = ''
      this.persistentHint = false
    },
    selectedRange(date) {
      if (this.dateArray.length === 2) {
        const tempDateArray: [string, string] = [
          this.dateArray[0],
          this.dateArray[1],
        ] // Add fixed length array to prevent type error for isBetween
        return dayjs(date).isBetween(...tempDateArray)
      }
      return ''
    },

    // MAX RANGE calc and reset

    maxRangeCalc(date, range) {
      // Calculate new min/max
      const tempMaxDate = dayjs(date)
          .add(range.value, range.unit)
          .subtract(1, 'day')
          .format('YYYY-MM-DD'),
        tempMinDate = dayjs(date)
          .subtract(range.value, range.unit)
          .add(1, 'day')
          .format('YYYY-MM-DD')
      // compare to current max
      this.calcMaxDate =
        this.maxDate != ''
          ? dayjs
              .min([dayjs(tempMaxDate), dayjs(this.maxDate)])
              .format('YYYY-MM-DD')
          : tempMaxDate
      // compare to current max
      this.calcMinDate =
        this.minDate != ''
          ? dayjs
              .max([dayjs(tempMinDate), dayjs(this.minDate)])
              .format('YYYY-MM-DD')
          : tempMinDate
    },
    resetRangeCalc() {
      this.calcMaxDate = this.maxDate
      this.calcMinDate = this.minDate
    },

    // VALIDATION

    validateMin(date) {
      if (this.minDate) return dayjs(date).isSameOrAfter(this.calcMinDate)
      else return true
    },
    validateMax(date) {
      if (this.maxDate) return dayjs(date).isSameOrBefore(this.calcMaxDate)
      else return true
    },
    validateMaxRange(date) {
      const range = this.maxRange,
        [startDate, endDate] = date.split('-'),
        desiredEndDate = dayjs(startDate).add(range.value, range.unit)
      return dayjs(endDate).isSameOrBefore(desiredEndDate, 'day')
    },
    validateAllowedDates(date) {
      return (
        this.allowedDates(date) &&
        this.validateMin(date) &&
        this.validateMax(date)
      )
    },

    // MAX RANGE params to text

    numberToText(number) {
      if (number < 11) {
        const dg = [
          'one',
          'two',
          'three',
          'four',
          'five',
          'six',
          'seven',
          'eight',
          'nine',
          'ten',
        ]
        return dg[number - 1]
      }
      return number
    },
    unitToText(unit) {
      switch (unit) {
        case 'd':
          return 'day'
        case 'w':
          return 'week'
        case 'M':
          return 'month'
        case 'Q':
          return 'quarter'
        case 'y':
          return 'year'
        case 'h':
          return 'hour'
        case 'm':
          return 'minute'
        case 's':
          return 'second'
        case 'ms':
          return 'millisecond'
        default:
          return unit
      }
    },
  },
})
